import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setDrawer, setSavedFilters } from 'store/entities/drawer/actions';
import { DrawerContentType } from 'components/mui/common/Drawer/DrawerWrapper';
import type { GetUserFiltersResWithContext } from 'types/userFilters';
import type { UserAgendaTableDataSchemaType } from 'types';
import { trackFilterOpened } from 'utils/posthog/metrics';

interface UseFilterDrawerActionsProps {
  filterDataSchema: UserAgendaTableDataSchemaType;
  agendaId: string;
  viewId: string;
  tableId?: string;
  isOverview: boolean;
  referencedFormItemsMap?: Map<string, string>;
  savedFilters?: GetUserFiltersResWithContext;
  type?: 'userAgenda' | 'table';
}

export const useFilterDrawerActions = ({
  filterDataSchema,
  agendaId,
  viewId,
  tableId,
  isOverview,
  referencedFormItemsMap,
  savedFilters,
  type = 'userAgenda',
}: UseFilterDrawerActionsProps) => {
  const dispatch = useDispatch();

  const handleFilterClick = useCallback(() => {
    try {
      trackFilterOpened({ process: 'filter', step: 'open', agendaId, viewId });
    } catch {
      /* noop */
    }
    dispatch(
      setDrawer({
        contentType: DrawerContentType.FILTER_DRAWER,
        isOpen: true,
        dataSchema: filterDataSchema,
        agendaId,
        viewId,
        tableId,
        entity: `${type}-${isOverview ? 'overview' : 'listing'}-${viewId}`,
        referencedFormItemsMap: referencedFormItemsMap ? Object.fromEntries(referencedFormItemsMap) : {},
        type,
      })
    );

    const savedFiltersItems = savedFilters?.viewId === viewId ? (savedFilters?.filters?.items ?? []) : [];
    dispatch(
      setSavedFilters({
        [`${type}-${isOverview ? 'overview' : 'listing'}-${viewId}`]: savedFiltersItems,
      })
    );
  }, [
    dispatch,
    filterDataSchema,
    agendaId,
    viewId,
    tableId,
    type,
    isOverview,
    referencedFormItemsMap,
    savedFilters?.viewId,
    savedFilters?.filters?.items,
  ]);

  return { handleFilterClick };
};
